import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Grid, Card, CardContent,
  Divider, Alert, Snackbar
} from '@mui/material';
import { Save } from '@mui/icons-material';
import HeaderPimpinan from '../components/HeaderPimpinan';
import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { AuthContext } from '../context/AuthContext';

const TemplateWhatsApp = () => {
  const { userData } = useContext(AuthContext);
  const [template, setTemplate] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Dummy data untuk live preview
  const dummyData = {
    nama: 'Budi Santoso',
    nomorPendaftaran: 'PMB-2023-001',
    asalSekolah: 'SMAN 1 Jakarta',
    jurusan: 'Teknik Informatika',
    tglDaftar: '2023-10-01'
  };

  useEffect(() => {
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    try {
      const docRef = doc(db, 'settings', 'wa_template');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setTemplate(docSnap.data().template);
      } else {
        // Fallback default if not exists
        setTemplate(`Halo {nama},\n\nKami dari bagian admin pendaftaran. Menghubungi terkait pendaftaran Anda dengan Nomor Pendaftaran: {nomorPendaftaran}.\n\nTerima kasih.`);
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      showSnackbar('Gagal memuat template', 'error');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const docRef = doc(db, 'settings', 'wa_template');
      await setDoc(docRef, {
        template: template,
        updatedAt: new Date().toISOString(),
        updatedBy: userData?.email || 'admin'
      });
      showSnackbar('Template berhasil disimpan', 'success');
    } catch (error) {
      console.error('Error saving template:', error);
      showSnackbar('Gagal menyimpan template', 'error');
    }
    setLoading(false);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const generatePreview = () => {
    let previewMsg = template;
    Object.keys(dummyData).forEach(key => {
      // Create global regex for {key}
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      previewMsg = previewMsg.replace(regex, dummyData[key]);
    });
    return previewMsg;
  };

  return (
    <Box>
      <HeaderPimpinan />
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
          Pengaturan Template WhatsApp
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Atur format pesan otomatis yang akan dikirimkan ke siswa saat tombol WhatsApp ditekan.
        </Typography>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Editor Pesan
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={10}
                variant="outlined"
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                placeholder="Ketik template pesan di sini..."
              />
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Save />}
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? 'Menyimpan...' : 'Simpan Template'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Panduan Variabel
              </Typography>
              <Typography variant="body2" gutterBottom>
                Gunakan variabel berikut di dalam pesan Anda (termasuk kurung kurawal). Sistem akan otomatis menggantinya dengan data asli siswa.
              </Typography>
              <Box sx={{ mt: 2, backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <Grid container spacing={1}>
                  <Grid item xs={5}><Typography variant="body2" fontWeight="bold">{'{nama}'}</Typography></Grid>
                  <Grid item xs={7}><Typography variant="body2">Nama lengkap pendaftar</Typography></Grid>
                  
                  <Grid item xs={5}><Typography variant="body2" fontWeight="bold">{'{nomorPendaftaran}'}</Typography></Grid>
                  <Grid item xs={7}><Typography variant="body2">Nomor pendaftaran</Typography></Grid>
                  
                  <Grid item xs={5}><Typography variant="body2" fontWeight="bold">{'{asalSekolah}'}</Typography></Grid>
                  <Grid item xs={7}><Typography variant="body2">Sekolah asal pendaftar</Typography></Grid>
                  
                  <Grid item xs={5}><Typography variant="body2" fontWeight="bold">{'{jurusan}'}</Typography></Grid>
                  <Grid item xs={7}><Typography variant="body2">Jurusan yang dipilih</Typography></Grid>
                  
                  <Grid item xs={5}><Typography variant="body2" fontWeight="bold">{'{tglDaftar}'}</Typography></Grid>
                  <Grid item xs={7}><Typography variant="body2">Tanggal pendaftaran</Typography></Grid>
                </Grid>
              </Box>
            </Paper>

            <Card variant="outlined" sx={{ borderColor: '#25D366' }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#25D366', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                  Live Preview
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ 
                  backgroundColor: '#e1f5fe', 
                  p: 2, 
                  borderRadius: 2, 
                  borderTopLeftRadius: 0,
                  whiteSpace: 'pre-wrap',
                  typography: 'body2'
                }}>
                  {generatePreview()}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right' }}>
                  *Data di atas hanya contoh
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplateWhatsApp;
